import { useState, useEffect } from "react";
import { Cloud, Droplets, Wind, Thermometer, Eye, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

// Weather icon mapping to emoji/icons
const getWeatherIcon = (iconCode, conditions) => {
  const iconMap = {
    "01d": "☀️", "01n": "🌙",
    "02d": "⛅", "02n": "☁️",
    "03d": "☁️", "03n": "☁️",
    "04d": "☁️", "04n": "☁️",
    "09d": "🌧️", "09n": "🌧️",
    "10d": "🌦️", "10n": "🌧️",
    "11d": "⛈️", "11n": "⛈️",
    "13d": "❄️", "13n": "❄️",
    "50d": "🌫️", "50n": "🌫️",
  };
  return iconMap[iconCode] || "🌡️";
};

// Get wind direction from degrees
const getWindDirection = (deg) => {
  const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const index = Math.round(deg / 45) % 8;
  return directions[index];
};

// Compact weather badge for lists
export const WeatherBadge = ({ temp, icon, conditions }) => {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-white/5 rounded-full text-xs">
      <span>{getWeatherIcon(icon, conditions)}</span>
      <span className="font-semibold">{temp}°</span>
    </div>
  );
};

// Detailed weather card
export const WeatherCard = ({ lat, lng, compact = false, className = "" }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!lat || !lng) return;

    const fetchWeather = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API}/weather/forecast`, {
          params: { lat, lon: lng }
        });
        setWeather(response.data);
      } catch (err) {
        console.error("Weather fetch error:", err);
        setError("Weather unavailable");
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, [lat, lng]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-4 ${className}`}>
        <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (error || !weather) {
    return null; // Silently fail - weather is supplementary info
  }

  if (compact) {
    return (
      <WeatherBadge 
        temp={weather.temp} 
        icon={weather.icon} 
        conditions={weather.conditions} 
      />
    );
  }

  return (
    <div className={`glass rounded-xl p-4 ${className}`}>
      {/* Main temp and conditions */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-4xl">{getWeatherIcon(weather.icon, weather.conditions)}</span>
          <div>
            <div className="text-3xl font-oswald font-bold">{weather.temp}°F</div>
            <div className="text-sm text-gray-400 capitalize">{weather.description}</div>
          </div>
        </div>
        <div className="text-right text-sm text-gray-500">
          <div>Feels like {weather.feels_like}°</div>
          <div>H: {weather.temp_max}° L: {weather.temp_min}°</div>
        </div>
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-white/10">
        <div className="flex items-center gap-2">
          <Wind className="w-4 h-4 text-cyan-400" />
          <div>
            <div className="text-sm font-semibold">{weather.wind_speed} mph</div>
            <div className="text-xs text-gray-500">{getWindDirection(weather.wind_deg)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Droplets className="w-4 h-4 text-blue-400" />
          <div>
            <div className="text-sm font-semibold">{weather.humidity}%</div>
            <div className="text-xs text-gray-500">Humidity</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-gray-400" />
          <div>
            <div className="text-sm font-semibold">{Math.round(weather.visibility / 1609)} mi</div>
            <div className="text-xs text-gray-500">Visibility</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Weather for a specific stop (pre-loaded data)
export const StopWeather = ({ weather, compact = false, showArrival = false }) => {
  if (!weather || weather.error) return null;

  const formatArrivalTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 bg-white/5 rounded-full text-xs">
        <span>{getWeatherIcon(weather.icon, weather.conditions)}</span>
        <span className="font-semibold">{weather.temp}°</span>
        {weather.arrival_time && (
          <span className="text-cyan-400 text-[10px]">@{formatArrivalTime(weather.arrival_time)}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
      <span className="text-2xl">{getWeatherIcon(weather.icon, weather.conditions)}</span>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold">{weather.temp}°F</span>
          <span className="text-sm text-gray-400 capitalize">{weather.description}</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
          <span className="flex items-center gap-1">
            <Wind className="w-3 h-3" />
            {weather.wind_speed} mph
          </span>
          <span className="flex items-center gap-1">
            <Droplets className="w-3 h-3" />
            {weather.humidity}%
          </span>
        </div>
      </div>
    </div>
  );
};

export default WeatherCard;
